import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CodeBlockEditorDescriptor,
  codeBlockPlugin,
  CreateLink,
  headingsPlugin,
  imagePlugin,
  InsertImage,
  InsertTable,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
  useCodeBlockEditorContext,
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'

import { Flex, Typography } from 'antd'
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { ToolboxClient } from '~/plugins/toolbox/client'
import { Toolbox } from '~/plugins/toolbox/shared'
import { AgentAssistant } from '../../AgentAssistant'
import { ImageAssistant } from '../../ImageAssistant'
import { useImageClick } from '../hooks/useImageClick'
import { Theme } from './Theme'

type Props = {
  value?: string
  defaultValue?: string
  placeholder?: string
  isReadonly?: boolean
  toolbarChildren?: ReactNode
  style?: CSSProperties
  isUpdating?: boolean
  agent?: OrganizationMemberFromContext
  lightToolbar?: boolean

  onChange?: (value: string) => void
  onLoaded?: (value: string) => void
  onAskAI?: (textSelected: string, guidelines?: string) => void
  onEditImage?: (imageSrc: string, prompt: string) => void
}

function ensureSafeValue(raw: string): string {
  const withSoftBreaks = raw?.replace(/([^\n])\n(?!\n)/g, '$1  \n')
  const unescaped = withSoftBreaks?.replace(/^\\([-*#>])/gm, '$1')
  const safe = unescaped?.replace(/&#x20;/g, ' ')

  return safe
}

export const MarkdownEditor: React.FC<Props> = ({
  value = '',
  defaultValue,
  placeholder,
  isReadonly = false,
  toolbarChildren,
  style = {},
  isUpdating = false,
  lightToolbar = false,
  agent,
  onChange,
  onLoaded,
  onAskAI,
  onEditImage,
}) => {
  const [isSetup, setSetup] = useState(false)
  const [isFilled, setFilled] = useState(
    Toolbox.Utility.isDefined(value) || Toolbox.Utility.isDefined(defaultValue),
  )

  const ref = useRef<MDXEditorMethods>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { clickedImage } = useImageClick(containerRef)

  const hasInitialized = useRef(false)
  useEffect(() => {
    const initial = value ?? defaultValue

    if (Toolbox.Utility.isDefined(initial)) {
      const currentMarkdown = ref.current?.getMarkdown()
      const formatted = initial.replace(/(?<!\n)\n(?!\n)/g, '  \n')
      const isContentDifferent = currentMarkdown !== formatted
      if (!hasInitialized.current || isContentDifferent) {
        ref.current?.setMarkdown(formatted)
        onLoaded?.(ref.current.getMarkdown())
        setFilled(true)
        setSetup(true)
      }

      hasInitialized.current = true
    }
  }, [value, defaultValue])

  const handleChangeDebounced = ToolboxClient.useDebounceFunction(
    async (valueUpdated: string) => {
      onChange?.(valueUpdated)
    },
    700,
  )

  const handleChange = (valueUpdated: string) => {
    const valueSafe = ensureSafeValue(valueUpdated)

    if (!isSetup) {
      setSetup(true)
      onLoaded?.(valueSafe)
      return
    }

    handleChangeDebounced(valueSafe)
    setFilled(Toolbox.Utility.isDefined(valueSafe))
  }

  const isPlaceholder = !isFilled && Toolbox.Utility.isDefined(placeholder)

  const PlainTextCodeEditorDescriptor: CodeBlockEditorDescriptor = {
    // always use the editor, no matter the language or the meta of the code block
    match: (language, meta) => true,
    // You can have multiple editors with different priorities, so that there's a "catch-all" editor (with the lowest priority)
    priority: 0,
    // The Editor is a React component
    Editor: props => {
      const cb = useCodeBlockEditorContext()
      // stops the proppagation so that the parent lexical editor does not handle certain events.
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            padding: '8px',
            fontFamily: 'monospace',
            fontSize: '14px',
            backgroundColor: '#f7f7f7',
            border: '0px solid #d9d9d9',
            borderRadius: '4px',
          }}
        >
          {props.code}
        </div>
      )
    },
  }

  return (
    <Theme
      ref={containerRef}
      className="prose w-full h-full"
      vertical
      style={{ ...style, position: 'relative' }}
    >
      {isPlaceholder && (
        <Typography.Text type="secondary" italic>
          {placeholder}
        </Typography.Text>
      )}
      <MDXEditor
        ref={ref}
        onChange={handleChange}
        markdown={value ?? defaultValue}
        readOnly={isReadonly}
        plugins={[
          codeBlockPlugin({
            codeBlockEditorDescriptors: [PlainTextCodeEditorDescriptor],
          }),
          toolbarPlugin({
            toolbarContents: () => (
              <Flex className="w-full" style={{ overflowX: 'auto' }}>
                <UndoRedo />
                {!lightToolbar && (
                  <>
                    <BoldItalicUnderlineToggles />
                    <ListsToggle />
                    <BlockTypeSelect />
                    <InsertTable />
                    <InsertImage />
                    <CreateLink />
                  </>
                )}

                {toolbarChildren}
              </Flex>
            ),
          }),
          imagePlugin(),
          headingsPlugin(),
          listsPlugin(),
          quotePlugin(),
          tablePlugin(),
          thematicBreakPlugin(),
          linkPlugin(),
          linkDialogPlugin(),
        ]}
      />
      {agent && agent.appSession?.status !== 'DISABLED' && (
        <AgentAssistant
          agent={agent}
          containerRef={containerRef}
          onAskAI={onAskAI}
          isUpdating={isUpdating}
        />
      )}

      {agent && agent.appSession?.status !== 'DISABLED' && clickedImage && (
        <ImageAssistant
          className="image-assistant"
          agent={agent}
          imageSrc={clickedImage.src}
          rect={clickedImage.rect}
          containerRef={containerRef}
          isUpdating={isUpdating}
          onEdit={onEditImage}
        />
      )}
    </Theme>
  )
}
